import { useMemo, useCallback, useEffect } from "react";
import FormBar from "@hs/form-bar";
import { useFormActions } from "hooks";
import { FormBarView, ActivityType, ItemNoteType } from "data/types";
import { getClassNames, getLocalizedText } from "data/utils";
import Dropdown from "@hs/dropdown";
import MenuItem from "react-bootstrap/lib/MenuItem";
import styles from "./Activity.module.css";

const components = {
    Container: "aside",
};
/**
 * renders an activity filter bar
 *
 * @param {Props} { Activity, className }
 */
export const Activity = ({
    className,
    isReadonly,
    commentType,
    setCommentType,
    activityType,
    setActivityType,
    currentUser,
    setCurrentUser,
    customFilter,
    setCustomFilter,
    showHistory,
    showNotes,
    items,
    itemsToRender,
    isPrefiltered,
}: Props) => {
    const actions = useFormActions(FormBarView.Activity);
    const isActive = useCallback(
        (menuItem) =>
            (commentType ?? true) === (menuItem.commentType ?? true) &&
            (activityType ?? true) === (menuItem.activityType ?? true) &&
            (currentUser ?? true) === (menuItem.user ?? true) &&
            (customFilter ?? true) === (menuItem.customFilter ?? true),
        [customFilter, commentType, activityType, currentUser]
    );
    const users = useMemo(
        () => [
            ...new Map(
                itemsToRender.map((item) => [
                    item["userId"],
                    {
                        id: item.userId,
                        name: item.userName,
                    },
                ])
            ).values(),
        ],
        [itemsToRender]
    );
    const customFilters = useMemo(
        () => [
            ...new Map(
                items
                    .filter((item) => item.extras?.$filterKey)
                    .map((item) => [
                        item.extras.$filterKey,
                        {
                            id: item.extras?.$filterKey,
                            name: item.extras?.$filterLabel,
                        },
                    ])
            ).values(),
        ],
        [items]
    );
    const { toggleName, menuItems } = useMemo(() => {
        const menuItems = [
            {
                commentType: null,
                activityType: null,
                user: null,
                customFilter: null,
                label: getLocalizedText("$activity:filter.all"),
            },
        ];
        // Add available history filters
        if (!isPrefiltered && showHistory) {
            Object.values(ActivityType).forEach((activityType) => {
                if (activityType === ActivityType.comment) return;
                if (activityType === ActivityType.system) return;
                if (
                    !items.some((item) =>
                        activityType === ActivityType.history
                            ? item.what !== ActivityType.comment
                            : item.what === activityType
                    )
                )
                    return;
                menuItems.push({
                    commentType: null,
                    activityType,
                    user: null,
                    customFilter: null,
                    label: getLocalizedText(`$activity:filter.${activityType}`),
                });
            });
        }
        // Add system filters
        if (
            !isPrefiltered &&
            showNotes &&
            items.some((item) => item.what === ActivityType.system)
        ) {
            menuItems.push({
                commentType: null,
                activityType: ActivityType.system,
                user: null,
                customFilter: null,
                label: getLocalizedText(
                    `$activity:filter.${ActivityType.system}`
                ),
            });
        }
        // Add available comment filters
        if (
            !isPrefiltered &&
            showNotes &&
            items.some((item) => item.what === ActivityType.comment)
        ) {
            menuItems.push({
                commentType: null,
                activityType: ActivityType.comment,
                user: null,
                customFilter: null,
                label: getLocalizedText(
                    `$activity:filter.${ActivityType.comment}.all`
                ),
            });
            Object.values(ItemNoteType).forEach((commentType) => {
                if (
                    !items.some(
                        (item) =>
                        {
                            /* istanbul ignore else */
                            if (item.extras === null) {
                                return ItemNoteType.Default === commentType;
                            }
                            /* istanbul ignore else */
                            if (item.extras) {
                                return item.extras.$type === commentType;
                            }
                            return false;
                        }
                    )
                )
                    return;
                menuItems.push({
                    commentType,
                    activityType: ActivityType.comment,
                    user: null,
                    customFilter: null,
                    label: getLocalizedText(
                        `$activity:filter.${ActivityType.comment}.${commentType}`
                    ),
                });
            });
        }
        // Add available user filters
        users.forEach((user) => {
            menuItems.push({
                commentType: null,
                activityType: null,
                user: user.id,
                customFilter: null,
                label: getLocalizedText(`$activity:filter.user`, user),
            });
        });
        // Add available custom filters
        customFilters.forEach((customFilter) => {
            menuItems.push({
                commentType: null,
                activityType: null,
                user: null,
                customFilter: customFilter.id,
                label: customFilter.name,
            });
        });
        const toggleName = menuItems.find(isActive)?.label;
        return {
            menuItems,
            toggleName,
        };
    }, [
        isActive,
        showHistory,
        showNotes,
        items,
        users,
        customFilters,
        isPrefiltered,
    ]);
    const onChange = useCallback(
        (menuItem) => {
            setCommentType(menuItem.commentType);
            setActivityType(menuItem.activityType);
            setCurrentUser(menuItem.user);
            setCustomFilter(menuItem.customFilter);
        },
        [setCommentType, setActivityType, setCurrentUser, setCustomFilter]
    );
    useEffect(() => {
        /* istanbul ignore else */
        if (toggleName == null) {
            onChange(menuItems[0]);
        }
    }, [toggleName, onChange, menuItems]);
    return (
        <FormBar
            className={getClassNames(className, styles.aside)}
            actions={actions}
            components={components}
        >
            <Dropdown className={styles.dropdown} disabled={!!isReadonly}>
                <Dropdown.Toggle>{toggleName}</Dropdown.Toggle>
                <Dropdown.Menu>
                    {menuItems.map((menuItem, idx) => (
                        <MenuItem
                            eventKey={idx + 1}
                            key={`formbar.activity.${
                                menuItem.commentType ?? "null"
                            }.${menuItem.activityType ?? "null"}.${
                                menuItem.user ?? "null"
                            }.${menuItem.customFilter ?? "null"}`}
                            onClick={() => onChange(menuItem)}
                            active={isActive(menuItem)}
                        >
                            {menuItem.label}
                        </MenuItem>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </FormBar>
    );
};
Activity.displayName = "FormBar.Activity";
export default Activity;
